import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumb';
import DataState from '../../components/DataState';
import { HoneyPot, FormNote } from '../../components/FormParts';
import RightArrow from '../../components/SVG';
import { formValues, submitApplication } from '../../lib/api/forms';
import { useCollections } from '../../lib/api/collections';
import { useSiteSettings } from '../../lib/api/settings';

const districts = [
  'Ampara', 'Anuradhapura', 'Badulla', 'Batticaloa', 'Colombo', 'Galle', 'Gampaha', 'Hambantota', 'Jaffna',
  'Kalutara', 'Kandy', 'Kegalle', 'Kilinochchi', 'Kurunegala', 'Mannar', 'Matale', 'Matara', 'Monaragala', 'Mullaitivu',
  'Nuwara Eliya', 'Polonnaruwa', 'Puttalam', 'Ratnapura', 'Trincomalee', 'Vavuniya',
];
const alYears = ['2026', '2025', '2024', '2023', '2022', '2021', '2020', '2019'];
const plain = (list) => list.map((x) => ({ value: x, label: x }));

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

// options: [{ value, label }] makes a drop-down list; otherwise a text box (type: text, date, tel, email).
const Field = ({ name, label, type, options, half, required, maxLength, defaultValue }) => (
  <div className={half ? 'col-xl-6' : 'col-xl-12'}>
    <div className="it-student-regiform-item">
      <label>{label}</label>
      {options ? (
        <div className="postbox__select">
          <select name={name} defaultValue={defaultValue || ''}>
            <option value="">Select an option</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <input type={type || 'text'} name={name} required={required} maxLength={maxLength || 200} />
      )}
    </div>
  </div>
);

const InstructorRegisterMain = () => {
  const { contact } = useSiteSettings();
  const content = useCollections('programs');
  const [params] = useSearchParams();
  const formRef = useRef(null);
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [state, setState] = useState({ status: 'idle', message: '' });
  const [done, setDone] = useState(null);
  const ready = !content.loading && !content.error;

  // Turn the plain drop-down lists into the template's styled ones once the form is on the page.
  useEffect(() => {
    if (ready && !done && window.$) window.$('select').niceSelect();
  }, [ready, done]);

  if (!ready) return <DataState state={content} />;

  const degrees = content.programs.filter((p) => p.kind === 'degree').map((p) => ({ value: p.id, label: p.title }));
  const preselect = degrees.some((d) => d.value === params.get('program')) ? params.get('program') : '';

  const fail = (message) => setState({ status: 'error', message });

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length > MAX_FILES) {
      e.target.value = '';
      setFiles([]);
      return fail(`You can add up to ${MAX_FILES} documents.`);
    }
    const bad = picked.find((f) => !FILE_TYPES.includes(f.type) || f.size > MAX_BYTES);
    if (bad) {
      e.target.value = '';
      setFiles([]);
      return fail(`"${bad.name}" cannot be used. Documents must be PDF, JPG or PNG files under 10 MB.`);
    }
    setState({ status: 'idle', message: '' });
    return setFiles(picked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = formValues(formRef.current);
    const needs = [
      ['district', 'your district'], ['gender', 'your gender'], ['alYear', 'your A/L exam year'], ['alStream', 'your A/L stream'],
      ['choice1', 'your first choice degree'], ['paymentMethod', 'how you will pay'],
    ];
    const missing = needs.find(([key]) => !v[key]);
    if (missing) return fail(`Please choose ${missing[1]}.`);

    const choices = [v.choice1, v.choice2, v.choice3].filter(Boolean);
    if (new Set(choices).size !== choices.length) return fail('Please choose a different degree for each choice.');

    setState({ status: 'sending', message: '' });
    try {
      const result = await submitApplication(
        {
          fullName: `${v.firstName} ${v.lastName}`.trim(),
          email: v.email,
          phone: v.phone,
          homePhone: v.homePhone,
          addressLine1: v.addressLine1,
          addressLine2: v.addressLine2,
          city: v.city,
          district: v.district,
          postcode: v.postcode,
          nicPassport: v.nicPassport,
          dateOfBirth: v.dateOfBirth,
          gender: v.gender,
          guardianPhone: v.guardianPhone,
          alYear: v.alYear,
          alStream: v.alStream,
          qualifications: v.qualifications,
          programChoices: choices,
          paymentMethod: v.paymentMethod,
          consent: v.consent === 'on',
          website: v.website,
        },
        files
      );
      setDone(result);
      setState({ status: 'idle', message: '' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      fail(err.message);
    }
    return undefined;
  };

  const startAgain = () => {
    setDone(null);
    setFiles([]);
    setState({ status: 'idle', message: '' });
  };

  const resetForm = () => {
    formRef.current.reset();
    if (window.$) window.$('select').niceSelect('update');
    setFiles([]);
    setState({ status: 'idle', message: '' });
  };

  return (
    <main>
      <Breadcrumb title="Apply For A Degree" subTitle="Apply" />

      <div className="it-student-area it-instructor-style pt-120 pb-120">
        <div className="container">
          <div className="row">
            <div className="col-xl-12">
              <div className="it-student-bg">
                <h4 className="it-student-title">Apply For A Degree</h4>

                {done ? (
                  <div className="it-student-content">
                    <h4 className="it-student-subtitle">Thank you. We have your application.</h4>
                    <p>
                      Your reference number is <strong>{done.reference}</strong>. Please keep it and quote it if you call us.
                      Our admissions team will contact you by phone or email.
                    </p>
                    {done.failedFiles.length > 0 && (
                      <FormNote status="error">
                        These documents could not be uploaded: {done.failedFiles.join(', ')}. Please email them, with your
                        reference number, to <a href={`mailto:${contact.admissionsEmail}`}>{contact.admissionsEmail}</a>.
                      </FormNote>
                    )}
                    <button type="button" className="ed-btn-theme mt-30" onClick={startAgain}>
                      <span>
                        Send Another Application
                        <i>
                          <RightArrow />
                        </i>
                      </span>
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="it-student-content mb-70">
                      <h4 className="it-student-subtitle">Fields with * are required</h4>
                      <p>Fill in your details, add certified copies of your certificates, and send it to us.</p>
                    </div>
                    <form ref={formRef} onSubmit={handleSubmit}>
                      <div className="it-student-regiform mb-40">
                        <h4 className="it-student-regiform-title">Contact Details</h4>
                        <div className="it-student-regiform-wrap">
                          <div className="it-student-regiform-item">
                            <label>Email *</label>
                            <input type="email" name="email" required maxLength={200} />
                          </div>
                          <div className="it-student-regiform-item">
                            <label>Mobile Phone *</label>
                            <input type="tel" name="phone" required maxLength={30} />
                          </div>
                          <div className="it-student-regiform-item">
                            <label>Home Phone (optional)</label>
                            <input type="tel" name="homePhone" maxLength={30} />
                          </div>
                        </div>
                      </div>
                      <div className="it-student-regiform">
                        <h4 className="it-student-regiform-title">Personal Details</h4>
                        <div className="it-student-regiform-wrap">
                          <div className="row">
                            <Field name="firstName" label="First Name *" half required maxLength={100} />
                            <Field name="lastName" label="Last Name *" half required maxLength={100} />
                            <Field name="dateOfBirth" label="Birth Date *" type="date" half required maxLength={10} />
                            <Field name="gender" label="Gender *" half options={[{ value: 'female', label: 'Female' }, { value: 'male', label: 'Male' }]} />
                            <Field name="district" label="Resident District *" options={plain(districts)} />
                            <Field name="addressLine1" label="Address 1 *" half required />
                            <Field name="addressLine2" label="Address 2 (optional)" half />
                            <Field name="city" label="City *" required maxLength={100} />
                            <Field name="postcode" label="Postcode (optional)" maxLength={20} />
                            <Field name="nicPassport" label="NIC or Passport Number *" required maxLength={30} />
                            <Field name="guardianPhone" label="Parent or Guardian Phone *" type="tel" required maxLength={30} />
                            <Field name="alYear" label="A/L Exam Year *" options={plain(alYears)} />
                            <Field name="alStream" label="A/L Stream *" options={plain(['Arts', 'Commerce', 'Science', 'Technology'])} />
                            <Field name="choice1" label="First Choice Degree *" options={degrees} defaultValue={preselect} />
                            <Field name="choice2" label="Second Choice Degree (optional)" options={degrees} />
                            <Field name="choice3" label="Third Choice Degree (optional)" options={degrees} />
                            <Field
                              name="paymentMethod"
                              label="How Will You Pay? *"
                              options={[
                                { value: 'self', label: 'Self-finance' },
                                { value: 'sponsor', label: 'Sponsorship' },
                                { value: 'loan', label: 'Government interest-free student loan' },
                              ]}
                            />
                          </div>
                          <div className="it-student-regiform-item">
                            <label>Other Qualifications (optional)</label>
                            <textarea name="qualifications" rows={3} maxLength={2000} className="sc-textarea"></textarea>
                          </div>
                          <div className="col-xl-12">
                            <div className="postbox__resume-wrap mb-20 d-flex align-items-center flex-wrap">
                              <div className="postbox__resume mr-20">
                                <input
                                  ref={fileRef}
                                  id="cv"
                                  type="file"
                                  multiple
                                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                  className="d-none"
                                  onChange={onFiles}
                                />
                                <label htmlFor="cv">
                                  <span>Upload Certificates</span>
                                </label>
                              </div>
                              <div className="postbox__resume border-transparent">
                                <label htmlFor="cv">
                                  <span>
                                    {files.length ? `${files.length} file${files.length > 1 ? 's' : ''} chosen` : 'No file chosen'}
                                  </span>
                                </label>
                              </div>
                            </div>
                            <p className="sc-file-help mb-40">
                              Up to {MAX_FILES} files (PDF, JPG or PNG), each under 10 MB. Certified copies of your birth
                              certificate and your G.C.E. results are needed.
                              {files.length > 0 && ` Chosen: ${files.map((f) => f.name).join(', ')}.`}
                            </p>
                          </div>
                          <div className="col-xl-12">
                            <div className="it-instructor-wrap">
                              <div className="it-signup-agree">
                                <h4 className="it-student-subtitle">Confirmation</h4>
                                <div className="form-check">
                                  <input className="form-check-input" type="checkbox" name="consent" id="consent" required />
                                  <label className="form-check-label" htmlFor="consent">
                                    I confirm that the details above are correct and I agree that SANASA Campus may use them
                                    to process my application.
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                          <HoneyPot />
                          <FormNote status={state.status}>{state.message}</FormNote>
                          <div className="it-student-regiform-btn mt-30">
                            <button type="submit" className="ed-btn-theme" disabled={state.status === 'sending'}>
                              <span>
                                {state.status === 'sending' ? 'Sending...' : 'Send Application'}
                                <i>
                                  <RightArrow />
                                </i>
                              </span>
                            </button>
                            <button type="button" className="ml-25 ed-btn-theme orange" onClick={resetForm}>
                              <span>
                                Cancel
                                <i>
                                  <RightArrow />
                                </i>
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};
export default InstructorRegisterMain;
